import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { SubTitle, Flex, Span, Icon, DropdownButton } from 'ui';
import { FormattedMessage, injectIntl } from 'react-intl';
import { extraSalesEmployeeTypes, additionalPartnerTypes, orderStatus } from 'utils/app-configs';
import styled from 'styled-components';
import isEqual from 'lodash.isequal';
import PartnerCard from './partner-card';

const Wrapper = styled(Flex)`
  flex-wrap: wrap;
  font-size: 12px;
`;

const Link = styled.span`
  color: #64747c;
  text-decoration: underline;
  cursor: pointer;
  user-select: none;
  text-transform: none;
  font-size: 19px;
  font-weight: 200;
  margin-left: 25px;
`;

export const Text = styled.div`
  margin-left: 15px;
  margin-top: 5px;
  color: ${p => p.theme.colors.alerts[2]};
  font-weight: ${p => p.theme.fontWeight.bolder};
`;

const SeverityIcon = styled(Icon)`
  color: ${p => p.theme.colors.alerts[2]};
`;

const ShowButton = ({ collapsed, toggleVisible }) => {
  if (collapsed) {
    return (
      <Link onClick={toggleVisible}>
        <FormattedMessage id='partners.SHOW' />
      </Link>
    );
  }
  return (
    <Link onClick={toggleVisible}>
      <FormattedMessage id='partners.HIDE' />
    </Link>
  );
};

ShowButton.propTypes = {
  collapsed: PropTypes.bool,
  toggleVisible: PropTypes.func,
};

const AdditionalPartners = ({
  partners,
  types,
  disabled,
  changePartner,
  removePartner,
  intl,
  orderType,
  activeStatus,
  readonly = false,
  isCollapsed = true,
}) => {
  const [collapsed, setCollapsed] = useState(isCollapsed);

  const getMandatoryWarnings = () => {
    if (!types || readonly) {
      return null;
    }
    return types.reduce((prev, t) => {
      if (!t.mandatory || !t.mandatory.length) {
        return prev;
      }

      if (t.mandatory.includes(orderType) || t.mandatory.includes('*')) {
        if (!partners?.find(p => p.partnerFunction.toUpperCase() === t.type.toUpperCase())) {
          return prev ? `${prev}, ${t.name}` : `Add ${t.name}`;
        }
      }
      return prev;
    }, null);
  };

  const getAddPartnerOptions = () => {
    const alreadyAddedPartnerTypes = partners?.map(p => p.partnerFunction.toUpperCase());
    const alreadyAddedSalesEmployees = partners?.filter(p =>
      extraSalesEmployeeTypes.includes(p.partnerFunction.toUpperCase()),
    );

    const typesWithSalesEmployees = types?.filter(
      t => ![...extraSalesEmployeeTypes, 'VE'].includes(t.type.toUpperCase()),
    );

    typesWithSalesEmployees?.sort((a, b) => a.name > b.name);
    typesWithSalesEmployees.push(types?.find(t => t.type.toUpperCase() === 'VE'));

    const extraSalesTypes =
      extraSalesEmployeeTypes?.filter(t => !alreadyAddedPartnerTypes.includes(t));

    typesWithSalesEmployees.push({
      type: extraSalesTypes?.length ?
        extraSalesTypes[0] :
        extraSalesEmployeeTypes[alreadyAddedSalesEmployees.length],
      name: intl.formatMessage(
        {
          id: 'partners.SALES_EMPLOYEES_LEFT',
        },
        { number: Math.floor(3 - alreadyAddedSalesEmployees.length, 0) },
      ),
      disabled: alreadyAddedSalesEmployees.length >= 3,
      allowed: alreadyAddedSalesEmployees.length < 3,
    });

    return typesWithSalesEmployees?.map(type => ({
      text: type.name,
      onClick: () =>
        changePartner({
          type: type.type,
          name: type.name.toUpperCase(),
          id: null,
          isNew: true,
        }),
      disabled:
        type.disabled ||
        alreadyAddedPartnerTypes.includes(type.type && type.type.toUpperCase()) ||
        !type.allowed,
    }));
  };

  if (!types || !types.length) {
    return null;
  }

  const canEditPartner = (maxEditState) => {
    if (!disabled && activeStatus && activeStatus.id) {
      return activeStatus.id <= maxEditState;
    }
    return false;
  };

  const data = partners
    ?.map((partner) => {
      const type = types?.find(
        t => t.type?.toLowerCase() === partner?.partnerFunction?.toLowerCase(),
      );
      const fullType = additionalPartnerTypes?.find(
        p => p.type.toUpperCase() === type?.type.toUpperCase(),
      );
      let maxEditState = fullType?.maxEditState ? fullType.maxEditState : orderStatus.executed;
      const salesEmployees = [...extraSalesEmployeeTypes, 'VE'];
      if (salesEmployees?.find(t => t?.toLowerCase() === type?.type?.toLowerCase())) {
        maxEditState = orderStatus.draft;
      }
      return {
        key: `${partner.number}_${type && type.type}`,
        partner,
        type,
        sort: fullType ? fullType.sort : 0,
        maxEditState,
      };
    })
    ?.sort((a, b) => a.sort - b.sort);

  const mandatoryMessage = getMandatoryWarnings();

  const toggleCollapsedHandler = () => {
    setCollapsed(prevCollapsed => !prevCollapsed);
  };

  return (
    <Flex ffcn>
      <SubTitle>
        <FormattedMessage id='partners.ADDITIONAL_PARTNERS' values={{ count: data.length }} />
        {partners && partners.length > 0 &&
          <ShowButton
            collapsed={collapsed}
            toggleVisible={toggleCollapsedHandler}
          />
        }
      </SubTitle>
      <>
        <Flex>
          {!readonly &&
            <DropdownButton
              asSeperate={false}
              text={intl.formatMessage({
                id: 'partners.ADD_PARTNER',
              })}
              subItems={getAddPartnerOptions()}
              disabled={disabled}
            />
          }
          {!readonly && mandatoryMessage &&
            <Text>
              <SeverityIcon type='exclamation-triangle' />
              <Span marginLeft={5}>{mandatoryMessage}</Span>
            </Text>
          }
        </Flex>
        {!collapsed &&
          <Wrapper>
            {data?.map((d, i) =>
              <PartnerCard
                position={i + 1}
                key={d.key}
                partner={d.partner}
                type={d.type}
                changePartner={changePartner}
                removePartner={removePartner}
                canEdit={canEditPartner(d.maxEditState)}
              />,
            )}
          </Wrapper>
        }
      </>
    </Flex>
  );
};

const areEqual = (prevProps, nextProps) => {
  if (
    isEqual(nextProps.partners, prevProps.partners) &&
    isEqual(nextProps.disabled, prevProps.disabled) &&
    isEqual(nextProps.types, prevProps.types) &&
    isEqual(nextProps.orderType, prevProps.orderType) &&
    isEqual(
      nextProps.activeStatus && nextProps.activeStatus.id,
      prevProps.activeStatus && prevProps.activeStatus.id,
    )
  ) {
    return true;
  }
  return false;
};

AdditionalPartners.propTypes = {
  partners: PropTypes.array,
  types: PropTypes.array,
  disabled: PropTypes.bool,
  changePartner: PropTypes.func,
  removePartner: PropTypes.func,
  intl: PropTypes.object,
  orderType: PropTypes.string,
  activeStatus: PropTypes.object,
  readonly: PropTypes.bool,
  isCollapsed: PropTypes.bool,
};

export default React.memo(injectIntl(AdditionalPartners), areEqual);
